import React, { useMemo, useState } from 'react';
import { Box, Button, Container, Divider, Pagination, Paper, Stack, TextField, Typography, Tooltip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

type VectorResult = {
  id: number;
  channel: string;
  user_id: string;
  username?: string | null;
  real_name?: string | null;
  text: string;
  timestamp: string;
  score: number;
};

const pageSizeDefault = 20;

/**
 * VectorSearch: A standalone page for semantic (vector) search over Slack messages.
 *
 * This component complements the existing keyword search page by calling the new
 * unified Flask endpoints under `/api/vector/*`. The page allows users to type a
 * free-form query, submit, and paginate through results scored by similarity.
 */
const VectorSearch: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<VectorResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = pageSizeDefault;
  const [ingesting, setIngesting] = useState(false);
  const [lastIngestInfo, setLastIngestInfo] = useState<string | null>(null);

  const totalPages = useMemo(() => {
    if (!totalResults) return 0;
    return Math.ceil(totalResults / pageSize);
  }, [totalResults, pageSize]);

  const handleSearch = async (pageOverride?: number) => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const resp = await fetch('/api/vector/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query, page: pageOverride ?? page, page_size: pageSize })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setResults(data.results || []);
      setTotalResults(data.total_results || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
    handleSearch(newPage);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    handleSearch(1);
  };

  const handleRebuildIndex = async () => {
    setIngesting(true);
    setLastIngestInfo(null);
    try {
      const resp = await fetch('/api/vector/ingest/rebuild', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ batch_size: 1000 })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setLastIngestInfo(`Indexed ${data.indexed_messages} messages → ${data.index_path}`);
    } catch (e) {
      console.error(e);
      setLastIngestInfo('Ingestion failed');
    } finally {
      setIngesting(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Vector Search
      </Typography>
      <Paper sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              label="Semantic query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </Button>
            <Tooltip title="Rebuild FAISS index from the SQLite corpus">
              <span>
                <Button variant="outlined" disabled={ingesting} onClick={handleRebuildIndex}>
                  {ingesting ? 'Rebuilding…' : 'Rebuild Index'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </form>
        <Divider sx={{ my: 2 }} />
        {lastIngestInfo && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {lastIngestInfo}
          </Typography>
        )}
        <Stack spacing={2}>
          {results.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  {r.channel} • {r.username || r.user_id} • {new Date(r.timestamp).toLocaleString()} • score {r.score.toFixed(4)}
                </Typography>
                <Typography>{r.text}</Typography>
              </Stack>
            </Paper>
          ))}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
            </Box>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default VectorSearch;


